import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  "group/button inline-flex shrink-0 items-center justify-center rounded-none border border-transparent bg-clip-padding text-base font-normal whitespace-nowrap transition-[color,background-color,border-color,translate] duration-(--motion-hover-duration) ease-hover outline-none select-none focus-visible:focus-ring active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-text [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        outline:
          "border-border bg-bg text-text hover:bg-band aria-expanded:bg-band",
        secondary:
          "border-secondary bg-transparent text-secondary hover:bg-band aria-expanded:bg-band",
        ghost:
          "text-text hover:bg-band aria-expanded:bg-band",
        destructive:
          "border-primary bg-transparent text-primary hover:bg-band",
        link: "h-auto px-0 text-text underline decoration-border underline-offset-4 hover:text-secondary hover:decoration-secondary",
      },
      size: {
        default:
          "h-10 gap-2 px-4 pointer-coarse:h-11 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3",
        xs: "h-7 pointer-coarse:h-11 gap-1 rounded-none px-2 text-xs in-data-[slot=button-group]:rounded-none has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 pointer-coarse:h-11 gap-1.5 rounded-none px-3 text-sm in-data-[slot=button-group]:rounded-none has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-11 gap-2 px-5 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        icon: "size-10 pointer-coarse:size-11",
        "icon-xs":
          "size-7 rounded-none in-data-[slot=button-group]:rounded-none pointer-coarse:size-11 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm":
          "size-8 rounded-none in-data-[slot=button-group]:rounded-none pointer-coarse:size-11",
        "icon-lg": "size-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
